import React, { useEffect, useState } from 'react';
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    TableSortLabel,
} from '@mui/material';
import { CustomAccordion, CustomButton, CustomReactSelect } from '../../../shared';
import {
    DECILE_TABLE_COLUMNS,
    SEGMENT_TABLE_COLUMNS,
    DUAL_DECILE_TABLE_COLUMNS,
    importanceOptions,
    investmentOptions
} from './constants';
import { useDispatch, useSelector } from 'react-redux';
import { getImportance, getIntensity, postImportance, postIntensity } from '../../../../redux/thunks/physiciansThunk';
import { actions as physicianAction  } from '../../../../redux/reducers/physicianReducer';
import { getSortedData } from '../../../../utility';

const handleImportanceFontColor = (type) => {
    switch (type?.toLowerCase()) {
        case 'high':
            return '#12B76A';
        case 'medium':
            return '#FB6514';
        case 'low':
            return "#C4320A";
        default:
            return '#FB6514';
    }
}

const EnhancedTableHead = ({ columns, onRequestSort, order, orderBy }) => {
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {columns.map((column, index) => (
                    <TableCell
                        key={index}
                        align='center'
                        sortDirection={orderBy === column.key ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === column.key}
                            direction={orderBy === column.key ? order : 'asc'}
                            onClick={createSortHandler(column.key)}
                        >
                            {column.value}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

const decileTableComponent = (DECILE_TABLE_COLUMNS, DECILE_TABLE_DATA, total, handImportanceChange,handleRequestSort,order,orderBy) => {
    const sortedData = React.useMemo(() => {
        return getSortedData(orderBy, order, DECILE_TABLE_DATA);
    }, [order, orderBy, DECILE_TABLE_DATA]); 
return(
    <TableContainer component={Paper}>
        <Table>
        <EnhancedTableHead
                columns={DECILE_TABLE_COLUMNS}
                onRequestSort={handleRequestSort}
                order={order}
                orderBy={orderBy}
            />
            <TableBody>
                {sortedData?.map((row, index) => (
                    <TableRow key={row?.id}>
                        <TableCell align='center'>
                            <Typography className='text-400fw-14fs-20lh-bg-gray-500 padding-tb-3-rl-12' >{row?.decile}</Typography>
                        </TableCell>
                        <TableCell className='text-sm-semi-bold gray-700' align='center' >
                            {row?.physician_qty}
                        </TableCell>
                        <TableCell className='text-sm-medium w-40' align='center' >
                            <CustomReactSelect
                                options={importanceOptions}
                                handleFilter={(option) => handImportanceChange(row, option, "decile")}
                                selectedOption={importanceOptions.find(item=> item.value === row?.importance)}
                            />
                            
                        </TableCell>
                    </TableRow>
                ))}
                <TableRow>
                    <TableCell className='text-sm-semi-bold gray-700' align='right' > Total: </TableCell>
                    <TableCell className='text-sm-semi-bold gray-700 bg-primary-50' >{total}</TableCell>
                </TableRow>
            </TableBody>
        </Table>
    </TableContainer>
)
};

const segmentTableComponent = (SEGMENT_TABLE_COLUMNS, SEGMENT_TABLE_DATA, total, handImportanceChange,handleRequestSort,order,orderBy) => {
    const sortedData = React.useMemo(() => {
        return getSortedData(orderBy, order, SEGMENT_TABLE_DATA);
    }, [order, orderBy, SEGMENT_TABLE_DATA]); 
    return(
    <TableContainer component={Paper}>
        <Table>
        <EnhancedTableHead
                columns={SEGMENT_TABLE_COLUMNS}
                onRequestSort={handleRequestSort}
                order={order}
                orderBy={orderBy}
            />
            <TableBody>
                {sortedData?.map((row, index) => (
                    <TableRow key={row?.id}>
                        <TableCell className={`text-500fw-12fs-18lh primary-700 flex-row-center ${index === 0 ? 'height-71-5px' : 'height-71px'}`} align='center' >
                        <Box className="npi-table-segmentation-cell w-fit-content ">
                                {row?.segment}
                            </Box>
                        </TableCell>
                        <TableCell className='text-sm-semi-bold gray-700' align='center' >
                            {row?.physician_qty}
                        </TableCell>
                        <TableCell  className='text-sm-medium w-40' align='center' >
                            <CustomReactSelect
                                options={importanceOptions}
                                handleFilter={(option) => handImportanceChange(row, option, "segment")}
                                selectedOption={importanceOptions.find(item=> item.value === row.importance)}
                                
                            />
                        </TableCell>
                    </TableRow>
                ))}
                <TableRow>
                    <TableCell className='text-sm-semi-bold gray-700' align='right' > Total:</TableCell>
                    <TableCell className='text-sm-semi-bold gray-700 bg-primary-50' >{total}</TableCell>
                </TableRow>
            </TableBody>
        </Table>
    </TableContainer>
)};

const dualDecileTableComponent = (DUAL_DECILE_TABLE_COLUMNS, DUAL_DECILE_TABLE_DATA, total, handleIntensityChange,handleRequestSort,order,orderBy) => {
    const sortedData = React.useMemo(() => {
        return getSortedData(orderBy, order, DUAL_DECILE_TABLE_DATA);
    }, [order, orderBy, DUAL_DECILE_TABLE_DATA]); 
    return (
    <TableContainer component={Paper}>
        <Table>
        <EnhancedTableHead
                columns={DUAL_DECILE_TABLE_COLUMNS}
                onRequestSort={handleRequestSort}
                order={order}
                orderBy={orderBy}
            />
            <TableBody>
                {sortedData?.map((row, index) => (
                    <TableRow key={index}>
                        <TableCell align='center' className='text-400fw-14fs-20lh' sx={{ color: handleImportanceFontColor(row?.client_decile_importance) }}>
                            {row?.client_decile_importance?.charAt(0)?.toUpperCase() + row?.client_decile_importance?.slice(1)}
                        </TableCell>
                        <TableCell align='center' className='text-400fw-14fs-20lh' sx={{ color: handleImportanceFontColor(row?.digital_affinity_importance), }}>
                            {row?.digital_affinity_importance?.charAt(0)?.toUpperCase() + row?.digital_affinity_importance?.slice(1)}
                        </TableCell>
                        <TableCell className='text-sm-semi-bold gray-700' align='center' >
                            {row?.physician_qty}
                        </TableCell>
                        <TableCell className='text-500fw-14fs-20lh primary-600 w-30' align='center' >
                            <CustomReactSelect
                                options={investmentOptions}
                                 handleFilter={(option) => handleIntensityChange(row, option, "digital_intensity")}
                                selectedOption={investmentOptions?.find(item=> item.value === row?.digital_intensity)}
                                selectedValueColor ="#a04d97"
                            />
                           
                        </TableCell>
                        <TableCell className='text-500fw-14fs-20lh primary-600 w-30' align='center'>
                            <CustomReactSelect
                                options={investmentOptions}
                                handleFilter={(option) => handleIntensityChange(row, option, "promotion_intensity")}
                                selectedOption={investmentOptions.find(item=> item.value === row?.promotion_intensity)}
                                selectedValueColor ="#a04d97"
                            />
                        </TableCell>
                    </TableRow>
                ))}
                <TableRow>
                    <TableCell className='text-sm-semi-bold gray-700' align='right' colSpan={2} > Total:</TableCell>
                    <TableCell className='text-sm-semi-bold gray-700 bg-primary-50' >{total}</TableCell>
                </TableRow>
            </TableBody>
        </Table>
    </TableContainer>
)};

const decileAndSegment = (DECILE_TABLE_DATA,
    SEGMENT_TABLE_DATA,
    decileTotal, segmentTotal,
    handImportanceChange,
    handleSave,
    decileOrder,
    decileOrderBy,
    decileHandleRequestSort,
    segmentOrder,
    segmentOrderBy,
    segmentHandleRequestSort,
) => {
    let sortedDecile = [];
    if (Array.isArray(DECILE_TABLE_DATA) && DECILE_TABLE_DATA.length) {
        const updatedData = DECILE_TABLE_DATA.map(item => ({
            ...item,
            decile: item.decile === '0' ? 'Others' : item.decile
        }));
    
        const aggregatedData = Object.values(
            updatedData.reduce((acc, item) => {
                const key = `${item.decile}-${item.importance}`;
                if (!acc[key]) {
                    acc[key] = { ...item };
                } else {
                    acc[key].physician_qty += item.physician_qty;
                }
                return acc;
            }, {})
        );
        sortedDecile = aggregatedData.sort((a, b) => {
            const decileA = isNaN(parseInt(a.decile)) ? 10 : parseInt(a.decile);
            const decileB = isNaN(parseInt(b.decile)) ? 10 : parseInt(b.decile);
            return decileA - decileB;
        });
    }
    return (<>
        <Grid container spacing={'24px'} className='flex-row'>
            <Grid item xs={6}>
                <Box>
                    {decileTableComponent(DECILE_TABLE_COLUMNS, sortedDecile, decileTotal, handImportanceChange,decileHandleRequestSort,decileOrder,decileOrderBy)}
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box>
                    {segmentTableComponent(SEGMENT_TABLE_COLUMNS, SEGMENT_TABLE_DATA, segmentTotal, handImportanceChange,segmentHandleRequestSort,segmentOrder,segmentOrderBy)}
                </Box>
            </Grid>
        </Grid>
        <Box className="flex-row-end-center gap-12" >
            <Box>
                <CustomButton
                    title="Save Changes"
                    titleColor="#FFFFFF"
                    buttonColor="#AD509E"
                    handleClick={()=>handleSave()}
                />
            </Box>
        </Box>
    </>)
}

const dualDecile = (DUAL_DECILE_TABLE_DATA,total,
    handleIntensityChange,
    handleSaveIntensity,
    dualDecileOrder,
    dualDecileOrderBy,
    dualDecileHandleRequestSort,) => {
    return (<>
        <Grid container spacing={'24px'} className='flex-row' >
            <Grid item xs={12}>
                <Box>
                    {dualDecileTableComponent(DUAL_DECILE_TABLE_COLUMNS, DUAL_DECILE_TABLE_DATA, total, handleIntensityChange,dualDecileHandleRequestSort,dualDecileOrder,dualDecileOrderBy)}
                </Box>
            </Grid>
        </Grid><Box className="flex-row-end-center gap-12" >
            <Box>
                <CustomButton
                    title="Save Changes"
                    titleColor="#FFFFFF"
                    buttonColor="#AD509E"
                    handleClick={() => handleSaveIntensity()}
                />
            </Box>
        </Box>
    </>)
}

const DecilesAndSegments = (props) => {
    const { } = props;
    const dispatch = useDispatch();
    const { company } = useSelector(state => state.company);
    const { dualDeciles, decileAndSegmentData, totalDecilesQuantity, totalSegmentsQuantity, totalDualDecilesQuantity } = useSelector(state => state.physician)
    const [sorting, setSorting] = useState({
        decile: { orderBy: '', order: 'asc' },
        segment: { orderBy: '', order: 'asc' },
        dualDecile: { orderBy: '', order: 'asc' }
    });
    const handleRequestSort = (table, property) => {
        setSorting(prevState => ({
            ...prevState,
            [table]: {
                orderBy: property,
                order: sorting[table].orderBy === property && sorting[table].order === 'asc' ? 'desc' : 'asc'
            }
        }));
    };
    const handImportanceChange = (row, option, dataType) => {
        dispatch(physicianAction.handleImportanceChange({row: row, selected: option, dataType: dataType}))
    };

    const handleSave = () => {
        dispatch(postImportance({companyId: company.id, data: {segments:decileAndSegmentData.segment, deciles: decileAndSegmentData.decile}}))
    };

    const handleIntensityChange = (row, option, field) => {
        dispatch(physicianAction.handleIntensityChange({row: row, selected: option, field: field}))
    };

    const handleSaveIntensity = () => {
        dispatch(postIntensity({companyId: company.id, data: {intensities: dualDeciles}}))
    };

    useEffect(() => {
        if(company.id){
            dispatch(getImportance({companyId: company.id}));
            dispatch(getIntensity({companyId: company.id}));
        }

    },[company.id, dispatch])

    return (
        <>
            <Box className="padding-top-32px" >
                <CustomAccordion 
                    title="Select Importance for Deciles and Segments" 
                    component={decileAndSegment(decileAndSegmentData.decile, 
                        decileAndSegmentData.segment, 
                        totalDecilesQuantity, 
                        totalSegmentsQuantity, 
                        handImportanceChange, 
                        handleSave,
                        sorting.decile.order,
                        sorting.decile.orderBy,
                        (event, property) => handleRequestSort('decile', property),
                        sorting.segment.order,
                        sorting.segment.orderBy,
                        (event, property) => handleRequestSort('segment', property),
                    )} 
                    accordionContainerBackground="bg-blue-100"
                    acoordianDetailsBackground='bg-white'
                    accordionTitleColor='blue-500'
                    acoordianDetailsPadding='padding-t-8-b-16'
                    iconColor='#0BA5EC'
                />
            </Box>

            <Box className="padding-top-32px">
                <CustomAccordion 
                    title="Dual Deciling Allowing to Select for Level of Investment" 
                    component={dualDecile(dualDeciles,
                        totalDualDecilesQuantity,
                        handleIntensityChange,
                        handleSaveIntensity,
                        sorting.dualDecile.order,
                        sorting.dualDecile.orderBy,
                        (event, property) => handleRequestSort('dualDecile', property),
                    )} 
                    accordionContainerBackground="bg-green-100" 
                    acoordianDetailsBackground='bg-white'
                    accordionTitleColor='green-500'
                    acoordianDetailsPadding='padding-t-8-b-16'
                    iconColor='#12B76A'
                />
            </Box>
        </>
    );
}

export default DecilesAndSegments;